import { motion } from "framer-motion";
import { MARKET_COMPARISON } from "@/lib/site";
import { TrendingDown, Info, ArrowRight } from "lucide-react";

const MAX = 80000;

export default function PriceComparison() {
  return (
    <section id="mercado" data-testid="market-comparison-section" className="relative py-20 md:py-28">
      <div className="max-w-[1160px] mx-auto px-4 md:px-6">
        <div className="grid grid-cols-12 gap-6 mb-10 md:mb-14 items-end">
          <div className="col-span-12 md:col-span-8">
            <span className="chip mb-5">
              <span className="dot" /> Precio vs Mercado · CLP sin IVA
            </span>
            <h2 className="font-display text-3xl md:text-5xl lg:text-[56px] font-bold text-[#0B0B14] tracking-tight leading-[1.05]">
              Cuánto estás pagando de más. <span className="text-[#5A48C9]">Al año.</span>
            </h2>
          </div>
          <p className="col-span-12 md:col-span-4 text-[#3A3A48] leading-relaxed">
            Precios por unidad 1.0 mL con lidocaína. El diferencial se sostiene por
            volumen mensual comprometido y compra directa desde Corea.
          </p>
        </div>

        <div className="card-soft p-6 md:p-10">
          <div className="flex items-center justify-between mb-6">
            <p className="text-[11px] font-semibold uppercase tracking-widest text-[#6B6B7D]">
              Precio por unidad · Referencial B2B Chile 2025
            </p>
            <div className="hidden md:flex items-center gap-4 text-[10px] font-semibold uppercase tracking-widest text-[#6B6B7D]">
              <span className="flex items-center gap-1.5">
                <span className="w-3 h-3 rounded-sm bg-black/15" /> Mercado
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-3 h-3 rounded-sm bg-[#7C6AF5]" /> Clamignon
              </span>
            </div>
          </div>

          <ul className="space-y-5">
            {MARKET_COMPARISON.map((row, i) => {
              const pct = Math.min(100, (row.price / MAX) * 100);
              const savings = ((1 - row.price / MARKET_COMPARISON[0].price) * 100).toFixed(0);
              const isBrand = row.tone === "brand" || row.tone === "brand-hot";
              return (
                <motion.li
                  key={row.label}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.7, delay: i * 0.1 }}
                  data-testid={`market-row-${i}`}
                  className="grid grid-cols-12 items-center gap-4"
                >
                  <div className="col-span-12 md:col-span-4">
                    <p className={`font-display text-[15px] md:text-base font-bold ${isBrand ? "text-[#0B0B14]" : "text-[#3A3A48]"}`}>
                      {row.label}
                    </p>
                    <p className="text-[11px] font-semibold uppercase tracking-widest text-[#6B6B7D]">
                      {row.sub}
                    </p>
                  </div>

                  <div className="col-span-8 md:col-span-6">
                    <div className="relative h-9 rounded-full bg-black/[0.04] overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        whileInView={{ width: `${pct}%` }}
                        viewport={{ once: true }}
                        transition={{ duration: 1.1, delay: i * 0.15, ease: [0.22, 1, 0.36, 1] }}
                        className={`absolute inset-y-0 left-0 rounded-full ${
                          row.tone === "brand-hot"
                            ? "bg-gradient-to-r from-[#7C6AF5] to-[#5A48C9] shadow-[0_4px_16px_-4px_rgba(124,106,245,0.5)]"
                            : row.tone === "brand"
                            ? "bg-[#8B7CFF]"
                            : "bg-black/15"
                        }`}
                      />
                      {isBrand && i > 0 && (
                        <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[11px] font-bold text-white">
                          − {savings}%
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="col-span-4 md:col-span-2 text-right">
                    <p className={`font-display text-lg md:text-2xl font-bold tracking-tight ${isBrand ? "text-[#5A48C9]" : "text-[#3A3A48]"}`}>
                      {row.display}
                    </p>
                  </div>
                </motion.li>
              );
            })}
          </ul>

          <div className="mt-8 flex items-start gap-3 pt-6 border-t border-black/[0.06]">
            <Info className="w-4 h-4 text-[#6B6B7D] flex-shrink-0 mt-0.5" />
            <p className="text-[11px] leading-relaxed font-semibold uppercase tracking-wider text-[#6B6B7D]">
              Referencias B2B Chile 2025 · Fillers HA cross-linked equivalentes en
              concentración (24 mg/mL) y presentación (jeringa 1.0 mL con lidocaína).
            </p>
          </div>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            ["Ahorro 73–81%", "vs marca premium europea equivalente en G′ y concentración."],
            ["Volumen · único requisito", "≥ 100 unidades/mes te habilita. ≥ 500 accede al piso de precio."],
            ["Sin diluir grado clínico", "Mismo fabricante KGMP · Mismo registro ISP · Misma ficha técnica AEGENBIO."],
          ].map(([t, b], i) => (
            <div key={i} className="card-soft p-6">
              <div className="w-9 h-9 rounded-full bg-[#F1EDFF] flex items-center justify-center mb-4">
                <TrendingDown className="w-4 h-4 text-[#5A48C9]" strokeWidth={1.7} />
              </div>
              <p className="font-display text-lg font-bold text-[#0B0B14] mb-2">{t}</p>
              <p className="text-sm text-[#3A3A48] leading-relaxed">{b}</p>
            </div>
          ))}
        </div>

        <div className="mt-10 flex justify-center">
          <a
            data-testid="market-cta"
            href="#contacto"
            className="btn-pill btn-primary shine text-[15px] px-6 py-3.5"
          >
            Solicitar mi tramo mayorista
            <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </div>
    </section>
  );
}
